import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Phone, MessageCircle } from 'lucide-react';
import './GlobalReach.css';

const REGIONS = [
  { n:'Maharashtra & West India', f:'🟠', col:'var(--fire)', countries:['Pune','Mumbai','Nashik','Nagpur','Aurangabad','Kolhapur','Satara','Solapur','Sangli','Ahmednagar'], focus:'Spices, Jaggery, Channa, Cotton', office:'Pune HQ' },
  { n:'Pan India',                f:'🇮🇳', col:'var(--sky)',  countries:['Delhi','Bangalore','Chennai','Hyderabad','Ahmedabad','Jaipur','Lucknow','Kolkata','Surat','Indore'], focus:'All Products — Full Range', office:'Pan India' },
  { n:'International Markets',   f:'🌏', col:'#84CC16',     countries:['UAE','UK','USA','Singapore','Australia','Canada','Germany','Kuwait','Qatar','Bahrain'], focus:'Spices, Textiles, Handicrafts', office:'Export via Pune' },
];

const OFFICES = [
  { city:'Pune HQ', type:'Headquarters', addr:'Pune, Maharashtra — 411001, India', f:'🏢', hq:true },
  { city:'Mumbai',  type:'Trade Network', addr:'Mumbai, Maharashtra, India',         f:'🏙️' },
  { city:'Pan India', type:'Supply Network', addr:'Direct sourcing from farms & artisans across India', f:'🌾' },
  { city:'International', type:'Export Markets', addr:'UAE, UK, USA, Singapore & more', f:'✈️' },
];

const ROUTES = [
  { from:'Pune', to:'Mumbai',    vol:'Regular', prods:'All Products' },
  { from:'Pune', to:'Delhi',     vol:'Regular', prods:'Spices, Channa, Textiles' },
  { from:'Pune', to:'Bangalore', vol:'Regular', prods:'Food Products, Resin Art' },
  { from:'India', to:'UAE',      vol:'Export',  prods:'Spices, Jaggery, Textiles' },
  { from:'India', to:'UK/USA',   vol:'Export',  prods:'Organic Products, Handicrafts' },
  { from:'India', to:'Singapore',vol:'Export',  prods:'Spices, Silk Sarees' },
];

export default function GlobalReach() {
  const [a, setA] = useState(0);
  const r = REGIONS[a];
  return (
    <div className="page-enter" style={{ paddingTop: 80 }}>
      <section className="page-hero">
        <div className="page-hero__stripe"/><div className="page-hero__dots"/>
        <div className="container page-hero__inner">
          <div className="tag" style={{ marginBottom: 16 }}>Our Reach</div>
          <h1 className="page-hero__title">From Pune, <em>To The World</em></h1>
          <p className="page-hero__desc">
            Yashraj Traders supplies quality Indian products across Maharashtra, pan India and to international markets — delivering with care from our Pune headquarters.
          </p>
        </div>
      </section>

      {/* Region explorer */}
      <section className="gr-sec">
        <div className="container">
          <div className="gr-layout">
            <div className="gr-globe">
              <div className="gr-globe-inner">
                <span style={{ fontSize:80, animation:'float 7s ease-in-out infinite', display:'block' }}>🚢</span>
                <div className="gr-globe-n">Pune</div>
                <div className="gr-globe-s">Maharashtra · India</div>
              </div>
            </div>
            <div className="gr-regions">
              {REGIONS.map((rg, i) => (
                <button key={i} className={`gr-rb ${a===i?'gr-rb--on':''}`}
                  style={a===i?{borderColor:rg.col}:{}}
                  onClick={() => setA(i)}>
                  <span style={{ fontSize:22 }}>{rg.f}</span>
                  <div>
                    <div className="gr-rb__n">{rg.n}</div>
                    <div className="gr-rb__s">{rg.countries.length} locations · {rg.office}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="gr-detail" style={{ borderColor: r.col }}>
            <div className="gr-detail__top">
              <span style={{ fontSize:40 }}>{r.f}</span>
              <div>
                <h3 style={{ fontFamily:'var(--sans)', fontSize:20, fontWeight:800, color:'var(--text)', letterSpacing:'-0.3px' }}>{r.n}</h3>
                <div style={{ fontSize:13, color:r.col, fontWeight:700 }}>{r.office}</div>
              </div>
              <div className="gr-detail__focus"><strong>Products:</strong> {r.focus}</div>
            </div>
            <div className="gr-countries">
              {r.countries.map((c,i) => <span key={i} className="gr-ctag">{c}</span>)}
            </div>
          </div>
        </div>
      </section>

      {/* Offices / presence */}
      <section style={{ padding:'88px 0', background:'var(--white)' }}>
        <div className="container">
          <div style={{ textAlign:'center', marginBottom:48 }}>
            <div className="tag" style={{ marginBottom:14 }}>Our Presence</div>
            <h2 className="display">Where We Operate</h2>
          </div>
          <div className="gr-offices">
            {OFFICES.map((o, i) => (
              <div key={i} className={`gr-office ${o.hq?'gr-office--hq':''}`}>
                <div style={{ fontSize:28, marginBottom:12 }}>{o.f}</div>
                <div className="gr-o-type">{o.type}</div>
                <div className="gr-o-city">{o.city}</div>
                <div className="gr-o-addr"><MapPin size={12}/>{o.addr}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supply routes */}
      <section style={{ padding:'88px 0', background:'var(--off)' }}>
        <div className="container">
          <div style={{ marginBottom:44 }}>
            <div className="tag" style={{ marginBottom:14 }}>Key Routes</div>
            <h2 className="display">Our Delivery <em>Corridors</em></h2>
          </div>
          <div className="gr-routes">
            {ROUTES.map((r, i) => (
              <div key={i} className="gr-route">
                <div className="gr-r-path">
                  <span>{r.from}</span>
                  <div className="gr-r-line"/>
                  <ArrowRight size={13} color="var(--fire)"/>
                  <span>{r.to}</span>
                </div>
                <div className="gr-r-vol">{r.vol}</div>
                <div className="gr-r-prods">{r.prods}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="page-cta">
        <div className="container page-cta__inner">
          <h2 className="page-cta__title">We Deliver to Your Location</h2>
          <p className="page-cta__sub">Wherever you are in India or abroad — reach out and we'll make it happen.</p>
          <div style={{ display:'flex', gap:14, justifyContent:'center', flexWrap:'wrap' }}>
            <Link to="/contact" className="btn btn-white">Send Enquiry <ArrowRight size={15}/></Link>
            <a href="https://wa.me/919226318469" className="btn btn-outline-white" target="_blank" rel="noreferrer">
              <MessageCircle size={14}/> WhatsApp Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
